import { useFocusManager } from '@react-aria/focus';
import { HStack, Icon, mergeEventHandlers, VStack, Text, FormRow } from '@cad-arc';
import React, { useCallback, useRef } from 'react';
import { FieldPath, FieldValues, useFormState } from 'react-hook-form';
import { EventPanelMetadata } from '@mark43/cad-api';
import { PrioritizedLocationCautionMarker } from '../../common/components/location-caution-marker';
import { genericMemo } from '../../common/helpers/memo';
import { LocationSelect } from '../../location-select/components/location-select';
import { LocationStoreView } from '../../location-select/types/locations';
import { CautionLevelAttrId } from '../../socket/stream-types';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { LocationSelectV1 } from '../../location-select/components/location-select-v1';
import {
    getLocationSummaryFirstLineText,
    getLocationSummarySecondLineText,
    getLocationSummaryThirdLineText,
} from '../../location-select/helpers/location-summary';
import { getLocationSummary } from '../../location-select/helpers/location-summary-v2';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useIntegratedForm } from '../use-integrated-form';

type LocationSelectProps = React.ComponentProps<typeof LocationSelect>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    classificationStrategy?: LocationSelectProps['classificationStrategy'];
    locationSelectProps?: LocationSelectProps;
    showLabel?: boolean;
    subpremiseSupport?: boolean;
    placeInputOutside?: boolean;
    highestPriorityLocationCaution?: EventPanelMetadata['highestPriorityLocationCaution'];
    isNearbyLocationCautionPriority1?: EventPanelMetadata['isNearbyLocationCautionPriority1'];
    onClickCautionMarker?: React.MouseEventHandler;
};

export function LocationFormRowWrapper({
    subpremisesEnabled,
    children,
}: {
    subpremisesEnabled: boolean;
    children: React.ReactNode;
}) {
    if (!subpremisesEnabled) {
        return <FormRow>{children}</FormRow>;
    }

    return <>{children}</>;
}

const stopPropagation = <T extends React.MouseEvent | React.FocusEvent | React.KeyboardEvent>(
    e: T
) => {
    e.stopPropagation();
};

const LocationViewMode = React.memo(function LocationViewMode(
    props: {
        value: LocationStoreView;
        highestPriorityLocationCaution?: EventPanelMetadata['highestPriorityLocationCaution'];
        isNearbyLocationCautionPriority1?: EventPanelMetadata['isNearbyLocationCautionPriority1'];
    } & Pick<Props, 'onClickCautionMarker'>
): React.ReactElement {
    const { value } = props;
    const { subpremiseSupport } = useEnabledFeatures();
    const locationSummarySecondLineText = getLocationSummarySecondLineText(value);
    const locationSummaryThirdLineText = getLocationSummaryThirdLineText(value);
    const { firstLine, thirdLine } = getLocationSummary(value);

    const handleClick = useCallback(
        (e) => {
            return mergeEventHandlers(
                stopPropagation as React.MouseEventHandler,
                props.onClickCautionMarker
            )(e);
        },
        [props.onClickCautionMarker]
    );

    return (
        <VStack spacing={0} alignItems="flex-start">
            <HStack alignItems="flex-start">
                <Text>
                    {subpremiseSupport
                        ? firstLine?.toUpperCase()
                        : getLocationSummaryFirstLineText(value)}
                </Text>
                {value.areCoordinatesVerified ? (
                    <Icon
                        icon="VerifiedLocation"
                        color="positive.default"
                        data-testid="location-select-verified"
                    />
                ) : undefined}
                {props.highestPriorityLocationCaution || props.isNearbyLocationCautionPriority1 ? (
                    <PrioritizedLocationCautionMarker
                        // Stop propagation on `mousedown` because otherwise
                        // a focus event will get triggered on the editable,
                        // which puts the field into edit mode
                        onMouseDown={stopPropagation}
                        onClick={handleClick}
                        // Stop propagation on `focus` because otherwise
                        // when you tab through the form, when this button
                        // gets focused, it would put the field into edit mode
                        onFocus={stopPropagation}
                        // Stop propagation on `keydown` because otherwise
                        // when press `enter` or `space`, we start typing
                        // into the input
                        onKeyDown={stopPropagation}
                        highestPriorityLocationCaution={
                            props.highestPriorityLocationCaution as CautionLevelAttrId | undefined
                        }
                        isNearbyLocationCautionPriority1={props.isNearbyLocationCautionPriority1}
                    />
                ) : undefined}
            </HStack>
            {locationSummarySecondLineText ? (
                <Text fontSize="sm"> {locationSummarySecondLineText}</Text>
            ) : undefined}
            {locationSummaryThirdLineText ? (
                <Text variant="caption" fontSize="xs" color="tertiary">
                    {locationSummaryThirdLineText}
                </Text>
            ) : undefined}
            {subpremiseSupport ? (
                <Text variant="caption" fontSize="xs" color="tertiary">
                    {thirdLine.toUpperCase()}
                </Text>
            ) : null}
        </VStack>
    );
});

export const FormLocationSelect = genericMemo(function FormLocationSelect<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const { subpremiseSupport } = useEnabledFeatures();

    const {
        locationSelectProps,
        highestPriorityLocationCaution,
        isNearbyLocationCautionPriority1,
        ...formControlBaseProps
    } = { ...props, formControlProps: { size: undefined } };
    const locationSelectPropsMemo = React.useMemo(
        () => locationSelectProps ?? ({} as LocationSelectProps),
        [locationSelectProps]
    );
    const renderViewMode = React.useCallback(
        (value: LocationStoreView) => {
            return (
                <LocationViewMode
                    onClickCautionMarker={props.onClickCautionMarker}
                    value={value}
                    isNearbyLocationCautionPriority1={isNearbyLocationCautionPriority1}
                    highestPriorityLocationCaution={highestPriorityLocationCaution}
                />
            );
        },
        [
            highestPriorityLocationCaution,
            isNearbyLocationCautionPriority1,
            props.onClickCautionMarker,
        ]
    );

    const focusManager = useFocusManager();
    const moveFocusNext = useCallback(
        function moveFocusNext() {
            const el = document.activeElement;
            if (focusManager && el) {
                setTimeout(() => {
                    focusManager?.focusNext({ from: el, tabbable: true });
                }, 0);
            }
        },
        [focusManager]
    );
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const showLabel = !!(!subpremiseSupport ? true : props.asEditable || props.placeInputOutside);

    const form = useIntegratedForm().form;
    const errors = useFormState({ control: form.control }).errors;
    const hasFormErrors = Object.keys(errors).length !== 0;

    return (
        <IntegratedFormControl
            {...formControlBaseProps}
            overrideFormSize={subpremiseSupport}
            length={subpremiseSupport && !props.placeInputOutside ? undefined : props.length}
            showLabel={showLabel}
            renderViewMode={renderViewMode}
        >
            {(providedProps) =>
                subpremiseSupport ? (
                    <LocationSelect
                        {...providedProps}
                        {...locationSelectPropsMemo}
                        classificationStrategy={props.classificationStrategy ?? 'SINGLE_AGENCY'}
                        moveFocusNext={moveFocusNext}
                        popoverRef={popoverRef}
                        onBlur={(e) => {
                            if (!popoverRef.current?.contains(e.relatedTarget)) {
                                providedProps.onBlur?.(e);
                            }
                        }}
                        placeInputOutside={props.placeInputOutside}
                        cautionProps={{
                            highestPriorityLocationCaution,
                            isNearbyLocationCautionPriority1,
                            onClickCautionMarker: props.onClickCautionMarker,
                        }}
                        hasFormErrors={hasFormErrors}
                    />
                ) : (
                    <LocationSelectV1
                        {...providedProps}
                        {...locationSelectPropsMemo}
                        classificationStrategy={props.classificationStrategy ?? 'SINGLE_AGENCY'}
                        moveFocusNext={moveFocusNext}
                        popoverRef={popoverRef}
                        onBlur={(e) => {
                            if (
                                e.relatedTarget !== popoverRef.current ||
                                popoverRef.current === null
                            ) {
                                providedProps.onBlur?.(e);
                            }
                        }}
                    />
                )
            }
        </IntegratedFormControl>
    );
});
